import { lazy } from "react";

import {
  captureEvent,
  extractErrorProperties,
} from "context/posthog/captureEvent";
import { POSTHOG_EVENTS } from "context/posthog/events";

import type { ComponentType, LazyExoticComponent } from "react";

export function lazyRetry<T extends ComponentType<any>>(
  componentImport: () => Promise<{ default: T }> | Promise<any>,
  name: string,
  next?: (module: any) => { default: T }
): LazyExoticComponent<T> {
  const storageName = `retry-${name}-refreshed`;

  return lazy(
    () =>
      new Promise((resolve, reject) => {
        const hasRefreshed = JSON.parse(
          sessionStorage.getItem(storageName) || "false"
        );

        componentImport()
          .then((component) => {
            sessionStorage.setItem(storageName, "false");

            if (hasRefreshed) {
              captureEvent(POSTHOG_EVENTS.chunkLoadRecovered, {
                chunkName: name,
              });
            }

            resolve(next ? next(component) : component);
          })
          .catch((error) => {
            if (!hasRefreshed) {
              sessionStorage.setItem(storageName, "true");

              // The reload tears the page down, so the batched queue would drop
              // this. `sendBeacon` is the only transport that survives unload.
              captureEvent(
                POSTHOG_EVENTS.chunkLoadReloadTriggered,
                { chunkName: name, ...extractErrorProperties(error) },
                { transport: "sendBeacon", send_instantly: true }
              );

              return window.location.reload();
            }

            captureEvent(POSTHOG_EVENTS.chunkLoadFailed, {
              chunkName: name,
              ...extractErrorProperties(error),
            });

            reject(error);
          });
      })
  );
}
